import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  IconButton,
  useTheme,
  useMediaQuery,
  InputAdornment
} from '@mui/material';
import {
  Instagram as InstagramIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon,
  Save as SaveIcon,
  Close as CloseIcon
} from '@mui/icons-material';
const EditProfileDialog = ({ open, onClose, onSave, profile }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    bio: '',
    isProfilePublic: true,
    socialLinks: {
      instagram: '',
      facebook: '',
      twitter: '',
      linkedin: ''
    },
    preferences: {
      emailNotifications: true,
      pushNotifications: true,
      showWorkoutHistory: true
    }
  });

  // Initialize form with profile data when dialog opens
  useEffect(() => {
    if (open && profile) {
      setFormData({
        firstName: profile.firstName || '',
        lastName: profile.lastName || '',
        bio: profile.bio || '',
        isProfilePublic: profile.isProfilePublic !== undefined ? profile.isProfilePublic : true,
        socialLinks: {
          instagram: profile.socialLinks?.instagram || '',
          facebook: profile.socialLinks?.facebook || '',
          twitter: profile.socialLinks?.twitter || '',
          linkedin: profile.socialLinks?.linkedin || ''
        },
        preferences: {
          emailNotifications: profile.preferences?.emailNotifications !== undefined 
            ? profile.preferences.emailNotifications : true,
          pushNotifications: profile.preferences?.pushNotifications !== undefined 
            ? profile.preferences.pushNotifications : true,
          showWorkoutHistory: profile.preferences?.showWorkoutHistory !== undefined 
            ? profile.preferences.showWorkoutHistory : true
        }
      });
    }
  }, [open, profile]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };


  const handleSocialLinkChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      socialLinks: {
        ...prev.socialLinks,
        [name]: value
      }
    }));
  };


  const handleSubmit = () => {
    // Exclude firstName and lastName from save (they're not editable)
    const { firstName, lastName, ...saveData } = formData;
    onSave(saveData);
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth={isMobile ? "sm" : "md"} 
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2,
          margin: isMobile ? 0 : 2
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: `1px solid ${theme.palette.divider}`,
        pb: 1,
        pt: 1,
        backgroundColor: theme.palette.background.default
      }}>
        Edit Profile
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ 
        p: isMobile ? 2 : 3,
        '&.MuiDialogContent-root': {
          paddingTop: isMobile ? 2 : 3
        }
      }}>
        <Grid container spacing={isMobile ? 2 : 3} sx={{ width: '100%' }}>
          {/* Basic Information */}
          <Grid size={12}>
            <Typography variant="h6" color="primary" sx={{ mb: 2 }}>
              Basic Information
            </Typography>
            
            <Grid container spacing={isMobile ? 1.5 : 2}>
              {/* Name fields on first row - Read only */}
              <Grid size={{ xs: 12, sm: 6 }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    First Name
                  </Typography>
                  <Typography variant="body1" sx={{ 
                    p: 1.5, 
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    backgroundColor: 'action.hover',
                    minHeight: '40px',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    {formData.firstName}
                  </Typography>
                </Box>
              </Grid>
              
              <Grid size={{ xs: 12, sm: 6 }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Last Name
                  </Typography>
                  <Typography variant="body1" sx={{ 
                    p: 1.5, 
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    backgroundColor: 'action.hover',
                    minHeight: '40px',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    {formData.lastName}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            
            {/* Bio on second row */}
            <Grid container spacing={isMobile ? 1.5 : 2} sx={{ mt: 1 }}>
              <Grid size={12}>
                <TextField
                  name="bio"
                  label="Bio"
                  value={formData.bio}
                  onChange={handleChange}
                  fullWidth
                  multiline
                  rows={4}
                  variant="outlined"
                  placeholder="Tell us about yourself..."
                  helperText={`${formData.bio.length}/500 characters`}
                  inputProps={{ maxLength: 500 }}
                />
              </Grid>
            </Grid>
          </Grid>
          
          {/* Social Links */}
          <Grid size={12}>
            <Typography variant="h6" color="primary" sx={{ mt: 3, mb: 2 }}>
              Social Links
            </Typography>
            
            <Grid container spacing={isMobile ? 1.5 : 2}>
                {/* Instagram */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    name="instagram"
                    label="Instagram Username"
                    value={formData.socialLinks.instagram}
                    onChange={handleSocialLinkChange}
                    fullWidth
                    variant="outlined"
                    placeholder="username (without @)"
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <InstagramIcon sx={{ color: '#E1306C' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                
                {/* Facebook */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    name="facebook"
                    label="Facebook Username"
                    value={formData.socialLinks.facebook}
                    onChange={handleSocialLinkChange}
                    fullWidth
                    variant="outlined"
                    placeholder="username"
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <FacebookIcon sx={{ color: '#4267B2' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                
                {/* Twitter */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    name="twitter"
                    label="Twitter Username"
                    value={formData.socialLinks.twitter}
                    onChange={handleSocialLinkChange}
                    fullWidth
                    variant="outlined"
                    placeholder="username (without @)"
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <TwitterIcon sx={{ color: '#1DA1F2' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                
                {/* LinkedIn */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    name="linkedin"
                    label="LinkedIn Username"
                    value={formData.socialLinks.linkedin}
                    onChange={handleSocialLinkChange}
                    fullWidth
                    variant="outlined"
                    placeholder="username"
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LinkedInIcon sx={{ color: '#0077B5' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
          </Grid>
          
        </Grid>
      </DialogContent>
      
      <DialogActions sx={{ 
        px: isMobile ? 2 : 3, 
        py: isMobile ? 1.5 : 2, 
        borderTop: `1px solid ${theme.palette.divider}`,
        bgcolor: theme.palette.background.default,
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 1 : 0
      }}>
        <Button 
          onClick={onClose}
          variant="outlined"
          fullWidth={isMobile}
          sx={{ 
            borderRadius: 4,
            px: 3,
            textTransform: 'none',
            order: isMobile ? 2 : 1
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          color="primary"
          startIcon={<SaveIcon />}
          fullWidth={isMobile}
          sx={{ 
            borderRadius: 4,
            px: 3,
            ml: isMobile ? 0 : 2,
            textTransform: 'none',
            order: isMobile ? 1 : 2
          }}
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditProfileDialog;
